import type { FormEvent } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import AuthLayout from "@/layouts/AuthLayout";
import SocialButtons from "@/components/SocialButtons";
import { useAuth } from "@/store/auth";
import { errorMessage } from "@/api/client";

type Tab = "email" | "tourist";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>("email");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      navigate("/home", { replace: true }); // -> Home, not dashboard
    } catch (err) {
      setError(errorMessage(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-center">
        <h1 className="font-display text-3xl font-bold text-navy-900">Welcome Back!</h1>
        <p className="mt-1 text-sm text-slate-500">Login to continue your journey</p>
      </div>

      {/* Tabs */}
      <div className="mt-6 grid grid-cols-2 border-b border-slate-200 text-sm font-medium">
        <button
          onClick={() => setTab("email")}
          className={`pb-3 transition ${
            tab === "email"
              ? "border-b-2 border-iris-500 text-iris-600"
              : "text-slate-400 hover:text-navy-700"
          }`}
        >
          Email / Mobile
        </button>
        <button
          onClick={() => setTab("tourist")}
          className={`pb-3 transition ${
            tab === "tourist"
              ? "border-b-2 border-iris-500 text-iris-600"
              : "text-slate-400 hover:text-navy-700"
          }`}
        >
          Tourist ID
        </button>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div>
          <input
            type="text"
            autoComplete="username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={tab === "email" ? "Email or Mobile Number" : "Tourist ID (e.g. MA-DOM-2026-000123)"}
            className="input"
          />
        </div>

        <div>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="input pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute inset-y-0 right-3 grid place-items-center text-slate-400 hover:text-navy-700"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="mt-1.5 text-right">
            <a href="/#" className="text-sm font-medium text-iris-600 hover:text-iris-700">
              Forgot Password?
            </a>
          </div>
        </div>

        <button type="submit" disabled={loading} className="btn-primary w-full py-3">
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Login"}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs font-medium text-slate-400">
        <span className="h-px flex-1 bg-slate-200" />
        or continue with
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <SocialButtons />

      <p className="mt-8 text-center text-sm text-slate-500">
        Don&apos;t have an account?{" "}
        <Link to="/signup" className="font-semibold text-iris-600 hover:text-iris-700">
          Sign Up
        </Link>
      </p>

      <div className="mt-6 rounded-lg bg-lavender px-4 py-3 text-xs text-slate-500">
        <span className="font-semibold text-navy-800">Demo:</span> admin{" "}
        <code>9999999999 / Admin@12345</code> · vendor{" "}
        <code>9888888888 / Vendor@12345</code>
      </div>
    </AuthLayout>
  );
}